import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Reveal, ImageReveal, LineReveal } from "../components/motion/Reveal";
import BookingForm from "../components/BookingForm";
import { ROOMS, AMENITIES, IMG, CONTACT } from "../lib/site";
import { CheckCircle2, MessageCircle, Phone, Mail } from "lucide-react";

export default function Heritage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#booking") {
      setTimeout(() => {
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [hash]);

  return (
    <div className="bg-bg">
      {/* Direct-reserve ribbon */}
      <a
        href="#booking"
        data-testid="direct-reserve-banner"
        className="block fixed top-0 inset-x-0 z-40 pt-0 md:pt-0"
      >
        <div className="w-full bg-terracotta text-bg py-2 mt-[76px] md:mt-[84px] group hover:bg-navy transition-colors duration-500">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center justify-center gap-3 text-[11px] md:text-xs tracking-[0.22em] uppercase font-semibold">
            <span className="hidden md:inline-block h-px w-6 bg-bg/60 group-hover:bg-gold transition-colors"/>
            <span>Reserve direct · Save the 15% OTA commission</span>
            <span className="hidden md:inline">·</span>
            <span className="underline underline-offset-4 decoration-1 group-hover:text-gold transition-colors">Enquire below</span>
            <span className="group-hover:translate-x-1 transition-transform" aria-hidden>→</span>
          </div>
        </div>
      </a>

      {/* Hero */}
      <section className="relative pt-52 pb-20 md:pt-64 md:pb-28" data-testid="heritage-hero">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta mb-6">Heritage Hotel · 20 rooms</div>
            <LineReveal
              className="font-display leading-[0.9] tracking-[-0.02em]"
              lineClassName="block text-6xl md:text-7xl lg:text-[8.5rem] text-ink"
              lines={["Rooms carved", <span key="i" className="italic text-terracotta">out of history.</span>]}
            />
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.4}>
              <p className="text-ink-soft leading-relaxed">
                All eighteen rooms renovated to preserve the old architecture, air-conditioned, private bathrooms with running hot and cold water, free Wi-Fi.
                All meals are served in the traditionally decorated Darikhana.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Rooms — alternating layout */}
      <section className="pb-24 md:pb-32" data-testid="rooms-list">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 space-y-24 md:space-y-40">
          {ROOMS.map((r, i) => {
            const reverse = i % 2 === 1;
            return (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center" data-testid={`room-row-${r.id}`}>
                <div className={`md:col-span-7 ${reverse ? "md:order-2" : ""}`}>
                  <Reveal>
                    <div className="border border-[#E5E1D8] overflow-hidden">
                      <ImageReveal src={r.image} alt={r.name} className="aspect-[16/10]"/>
                    </div>
                  </Reveal>
                </div>
                <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
                  <Reveal delay={0.1}>
                    <div className="font-display font-bold text-6xl md:text-7xl text-[#E5E1D8] leading-none">0{i+1}</div>
                    <div className="overline text-terracotta mt-4">{r.subtitle}</div>
                    <h3 className="font-display font-bold text-4xl md:text-5xl mt-3 leading-[1.02] text-ink">{r.name}</h3>
                    <div className="mt-3 flex items-baseline gap-4">
                      <span className="font-display text-3xl text-ink">₹{r.price.toLocaleString("en-IN")}</span>
                      <span className="text-xs text-ink-soft">per room, per night</span>
                    </div>
                    <div className="mt-1 text-xs text-ink-soft">Extra person / bed: ₹{r.extraPerson.toLocaleString("en-IN")} · Size: {r.size}</div>

                    <ul className="mt-6 space-y-2.5">
                      {r.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm text-ink">
                          <CheckCircle2 size={16} className="mt-0.5 text-gold shrink-0"/>
                          {f}
                        </li>
                      ))}
                    </ul>

                    <a href="#booking" className="btn-pill btn-primary mt-8" data-testid={`room-book-${r.id}`}>
                      Reserve this room
                    </a>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Dining — Darikhana */}
      <section className="bg-bg py-24 md:py-32" data-testid="dining">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <div className="border border-[#E5E1D8] overflow-hidden">
                <ImageReveal src={IMG.restaurant} alt="Darikhana dining hall" className="aspect-[16/10]"/>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <div className="overline text-terracotta">Dining at the Haveli</div>
              <h2 className="font-display font-bold text-5xl md:text-6xl mt-4 leading-[0.95] text-ink">
                Darikhana.
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed max-w-md">
                The traditionally decorated <strong>Darikhana</strong> is our dining hall — carved
                stone pillars, lantern-lit alcoves, cobalt table linens and family portraits watching
                you eat. The best local Rajasthani vegetarian meals are served here.
              </p>
              <ul className="mt-8 space-y-2.5">
                <li className="flex items-start gap-3 text-sm text-ink"><CheckCircle2 size={16} className="mt-0.5 text-gold shrink-0"/>Traditional Rajasthani vegetarian meals</li>
                <li className="flex items-start gap-3 text-sm text-ink"><CheckCircle2 size={16} className="mt-0.5 text-gold shrink-0"/>Rooftop dining under the palace lights</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="bg-navy text-bg py-24 md:py-32 grain relative overflow-hidden" data-testid="amenities">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline text-gold">Amenities</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-[0.95] text-bg drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]">
              What is included.
            </h2>
            <p className="mt-6 text-bg/70 max-w-sm leading-relaxed">
              Modern comforts, layered quietly onto 250-year-old architecture. Nothing digital-first,
              nothing that shouts.
            </p>
          </div>
          <ul className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 border-t border-bg/15 pt-8">
            {AMENITIES.map((a, i) => (
              <Reveal key={a} delay={i*0.03}>
                <li className="flex items-start gap-3 text-sm text-bg">
                  <span className="mt-2 w-1 h-1 rounded-full bg-gold shrink-0"/>
                  <span>{a}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Booking form */}
      <section id="booking" className="py-24 md:py-32 scroll-mt-24" data-testid="booking-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5 md:sticky md:top-32 self-start">
            <div className="overline text-terracotta">Direct reservation</div>
            <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl mt-5 leading-[0.92] text-ink">
              Enquire<br/><span className="italic text-terracotta">directly.</span>
            </h2>
            <p className="mt-6 text-ink-soft max-w-md leading-relaxed">
              Send us your dates and we will reply personally
              within 24 hours with best rate, availability and any special arrangements.
            </p>

            <div className="mt-10 space-y-4">
              <a href={`tel:${CONTACT.phone.replace(/\s+/g,'')}`} className="flex items-center gap-4 text-ink hover:text-terracotta transition-colors" data-testid="booking-phone">
                <span className="w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center"><Phone size={16}/></span>
                <div>
                  <div className="overline text-ink-soft">Call</div>
                  <div className="font-display text-lg">{CONTACT.phone}</div>
                  <div className="font-display text-lg">{CONTACT.phone2}</div>
                </div>
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-ink hover:text-terracotta transition-colors" data-testid="booking-whatsapp">
                <span className="w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center"><MessageCircle size={16}/></span>
                <div>
                  <div className="overline text-ink-soft">WhatsApp</div>
                  <div className="font-display text-lg">+91 97833 55866</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-ink hover:text-terracotta transition-colors" data-testid="booking-email">
                <span className="w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center"><Mail size={16}/></span>
                <div>
                  <div className="overline text-ink-soft">Email</div>
                  <div className="font-display text-lg">{CONTACT.email}</div>
                </div>
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="bg-surface border border-[#E5E1D8] p-6 md:p-10 shadow-[0_8px_32px_rgba(28,31,30,0.06)]">
              <BookingForm />
            </div>
            <p className="mt-4 text-[11px] text-ink-soft">
              By submitting you agree that Haveli Braj Bhushanjee will email you a confirmation.
              Taxes as applicable are extra. Room allotment is subject to availability.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
